import React, { useCallback, useEffect, useState } from 'react';
import {
    Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Paper, TextField,
    ToggleButton, ToggleButtonGroup, Typography
} from '@mui/material';
import { config } from '../Constants';

const CAMPAIGN = 'set-your-price-2026-09';
const API = `${config.url}/api/v1/admin/product-updates/${CAMPAIGN}`;

const PREVIEW_WIDTHS = { desktop: 680, phone: 390 };

/**
 * Links in the preview open in a new tab instead of navigating the sandboxed frame. The frame
 * runs no scripts; the email has none anyway.
 */
const previewDocument = (html) => html.replace(/<head([^>]*)>/i, '<head$1><base target="_blank">');

/**
 * Sending a product-update email, one deliberate step at a time (#365):
 * look at the numbers -> preview the email (#375) -> send a test to yourself -> send a batch,
 * after typing the campaign key.
 * Nothing here sends on load, and the server refuses a batch unless it is told the current
 * remaining count, so a stale tab cannot send to a list nobody looked at.
 */
const AdminProductUpdates = ({ authHeaders }) => {
    const [audience, setAudience] = useState('ALL');
    const [preview, setPreview] = useState(null);
    const [confirmText, setConfirmText] = useState('');
    const [busy, setBusy] = useState(false);
    const [notice, setNotice] = useState(null);
    const [rendered, setRendered] = useState(null);
    const [previewView, setPreviewView] = useState('desktop');

    const call = useCallback(async (path, method = 'GET') => {
        const response = await fetch(`${API}/${path}`, { method, headers: authHeaders });
        const body = await response.json().catch(() => ({}));
        if (!response.ok) {
            throw new Error(body.message || `Request failed (${response.status})`);
        }
        return body.data;
    }, [authHeaders]);

    const loadPreview = useCallback(async () => {
        try {
            setPreview(await call(`preview?audience=${audience}`));
        } catch (error) {
            setNotice({ severity: 'error', text: error.message });
        }
    }, [audience, call]);

    useEffect(() => {
        loadPreview();
    }, [loadPreview]);

    const run = async (action) => {
        setBusy(true);
        setNotice(null);
        try {
            await action();
        } catch (error) {
            setNotice({ severity: 'error', text: error.message });
        } finally {
            setBusy(false);
            await loadPreview();
        }
    };

    const openPreview = async () => {
        setBusy(true);
        setNotice(null);
        try {
            setRendered(await call('render'));
        } catch (error) {
            setNotice({ severity: 'error', text: error.message });
        } finally {
            setBusy(false);
        }
    };

    const sendTest = () => run(async () => {
        const result = await call('test', 'POST');
        setNotice(result.sent
            ? { severity: 'success', text: 'Test sent to your own address. Check it in Gmail, including dark mode, before sending to anyone else.' }
            : { severity: 'error', text: `Test failed: ${result.error}` });
    });

    const sendBatch = () => run(async () => {
        const result = await call(`send?audience=${audience}&batchSize=50&confirmRemaining=${preview.eligible}`, 'POST');
        setConfirmText('');
        setNotice({
            severity: result.failed ? 'warning' : 'success',
            text: `Batch done: ${result.sent} sent, ${result.failed} failed, ${result.remaining} remaining.`
                + (result.failures?.length ? ` Failures: ${result.failures.join('; ')}` : '')
        });
    });

    const ready = Boolean(preview?.readyToSend);
    const canSend = ready && preview.eligible > 0 && confirmText.trim() === CAMPAIGN && !busy;

    return (
        <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 } }}>
            <Typography variant="h6" sx={{ mb: 0.5 }}>Product update: {CAMPAIGN}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                A marketing email, not an alert. Nobody who unsubscribed is included, and nobody can be sent this campaign twice.
            </Typography>

            {notice && <Alert severity={notice.severity} sx={{ mb: 2 }}>{notice.text}</Alert>}
            {preview?.blockers?.map((blocker) => (
                <Alert key={blocker} severity="warning" sx={{ mb: 2 }}>Cannot send yet: {blocker}</Alert>
            ))}

            <TextField select size="small" label="Audience" value={audience} disabled={busy}
                onChange={(event) => { setAudience(event.target.value); setConfirmText(''); }} sx={{ minWidth: 200, mb: 2 }}>
                <MenuItem value="ALL">Everyone</MenuItem>
                <MenuItem value="PREMIUM">Premium only</MenuItem>
                <MenuItem value="FREE">Free only</MenuItem>
            </TextField>

            {preview && (
                <Box sx={{ mb: 3 }}>
                    <Typography variant="body1" data-testid="product-update-counts">
                        <strong>{preview.eligible}</strong> will receive it · {preview.users} in this audience
                        · {preview.optedOut} unsubscribed · {preview.alreadySent} already sent
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Subject: "{preview.subjectExample}"{preview.sample?.length ? ` · e.g. ${preview.sample.join(', ')}` : ''}
                    </Typography>
                </Box>
            )}

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'flex-start' }}>
                <Button variant="outlined" onClick={openPreview} disabled={busy}>
                    1. Preview the email
                </Button>
                <Button variant="outlined" onClick={sendTest} disabled={!ready || busy}>
                    2. Send a test to me only
                </Button>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                    <TextField size="small" label={`Type ${CAMPAIGN} to confirm`} value={confirmText} disabled={!ready || busy}
                        onChange={(event) => setConfirmText(event.target.value)} sx={{ minWidth: 300 }}
                        inputProps={{ 'aria-label': 'Type the campaign key to confirm' }} />
                    <Button variant="contained" color="warning" onClick={sendBatch} disabled={!canSend}>
                        3. Send the next {Math.min(50, preview?.eligible || 0)}
                    </Button>
                </Box>
                <Typography variant="caption" color="text.secondary">
                    Sends go out 50 at a time. Press again for the next batch; the count above updates after each one.
                </Typography>
            </Box>

            <Dialog open={Boolean(rendered)} onClose={() => setRendered(null)} maxWidth="md" fullWidth
                aria-labelledby="product-update-preview-title">
                <DialogTitle id="product-update-preview-title" sx={{ pb: 1 }}>
                    Preview - nothing is sent
                    <Typography variant="body2" color="text.secondary" data-testid="product-update-preview-subject">
                        Subject: "{rendered?.subject}"
                    </Typography>
                </DialogTitle>
                <DialogContent dividers sx={{ bgcolor: '#f1f5f9', p: { xs: 1, sm: 2 } }}>
                    <ToggleButtonGroup size="small" exclusive value={previewView} sx={{ mb: 2, bgcolor: '#fff' }}
                        onChange={(_event, view) => view && setPreviewView(view)} aria-label="Preview view">
                        <ToggleButton value="desktop">Desktop</ToggleButton>
                        <ToggleButton value="phone">Phone</ToggleButton>
                        <ToggleButton value="text">Plain text</ToggleButton>
                    </ToggleButtonGroup>
                    {rendered?.blockers?.length > 0 && (
                        <Alert severity="warning" sx={{ mb: 2 }}>Still blocked from sending: {rendered.blockers.join(' ')}</Alert>
                    )}
                    {previewView === 'text' ? (
                        <Box component="pre" data-testid="product-update-preview-text" sx={{
                            m: 0, p: 2, bgcolor: '#fff', borderRadius: 1, whiteSpace: 'pre-wrap',
                            fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 13
                        }}>
                            {rendered?.text}
                        </Box>
                    ) : (
                        <Box component="iframe" title="Email preview" sandbox="allow-popups allow-popups-to-escape-sandbox"
                            srcDoc={previewDocument(rendered?.html || '')} sx={{
                                display: 'block', mx: 'auto', border: 0, borderRadius: 1, bgcolor: '#fff',
                                width: '100%', maxWidth: PREVIEW_WIDTHS[previewView], height: '70vh'
                            }} />
                    )}
                </DialogContent>
                <DialogActions>
                    <Typography variant="caption" color="text.secondary" sx={{ mr: 'auto', pl: 1 }}>
                        Rendered for your account - the same email a test sends. Gmail can still differ slightly, so send a test before the real thing.
                    </Typography>
                    <Button onClick={() => setRendered(null)}>Close</Button>
                </DialogActions>
            </Dialog>
        </Paper>
    );
};

export default AdminProductUpdates;
